<template>
  <div class="flex h-screen flex-col">
    <RevLoadingScreen
      v-if="isLoading"
      class="bg-surface-default-mid grow"
      :text="''"
    />

    <div v-else class="grow">
      <div v-if="error">
        <OutOfStock
          v-if="isOutOfStockError(error)"
          :cart-selection-id
          :listing-grade
          :listing-price
          :listing-public-id
          :offer-id
          @replaced="handleReplacement"
        />
        <GenericError v-else class="py-24" :status="error.statusCode" />
      </div>

      <CheckoutProvider v-else>
        <template
          #default="{
            addressList,
            items,
            summary,
            onUpdate,
            loading,
            shippableCountries,
            lastCollectionPoint,
            billableCountries,
          }"
        >
          <OutOfStock
            v-if="hasOutOfStockItem(items)"
            :cart-selection-id
            :listing-grade
            :listing-price
            :listing-public-id
            :offer-id
            @replaced="handleReplacement"
          />

          <NuxtPage
            v-else
            :address-list
            :billable-countries
            :discount="discountStore.discount"
            :discount-token="discountStore.token"
            :initial-address="
              getAddressFromCustomerDetails(
                addressList.deliver,
                customerDetails,
              )
            "
            :items
            :lastCollectionPoint
            :loading
            :payment-method="discountStore.paymentMethod"
            :shippable-countries
            :summary
            :swapOffer="swapStore.offer"
            @update="onUpdate"
          />
        </template>
      </CheckoutProvider>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { NuxtError } from '#app'
import { onMounted, ref } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import CheckoutProvider from '~/scopes/checkout/components/CheckoutProvider/CheckoutProvider.vue'
import { useDiscountStore } from '~/scopes/checkout/stores/discountStore'
import { useSwapStore } from '~/scopes/checkout/stores/swapStore'
import { hasOutOfStockItem } from '~/scopes/checkout/utils/cart'

import GenericError from '../GenericError/GenericError.vue'

import {
  getAddressFromCustomerDetails,
  getCountryFromBouyguesCountry,
} from './InStoreBouyguesTelecom.utils'
import OutOfStock from './component/OutOfStock/OutOfStock.vue'

const swapStore = useSwapStore()
const discountStore = useDiscountStore()

const $httpFetch = useHttpFetch()
const tracking = useTracking()

const isLoading = ref(true)
const error = ref()

type CustomerDetails =
  ApiSpecs['/api/mobile-plan/in-store/v1/magic-links/:magicLinkId']['GET']['response']['customerDetails']

const props = defineProps<{
  listingPk: number
  listingPublicId: string
  listingGrade: number
  listingPrice: MonetaryAmount
  offerId: string
  customerDetails: CustomerDetails
  shopId: string | null
  cartSelectionId: string
}>()

function isOutOfStockError(err: NuxtError<{ type: string }>): boolean {
  if (!err.data) return false
  if (!err.data.type) return false

  return (
    err.data.type === '/errors/cart/add-product-stock' ||
    err.data.type === '/errors/cart/add-product-offline' ||
    err.data.type === '/errors/cart/add-product-price'
  )
}

async function addServiceToCart(listingPublicId: string) {
  await $httpFetch('/bm/cart/services', {
    method: 'POST',
    body: {
      type: 'MOBILE_PLAN',
      listingPublicId,
      offerId: props.offerId,
    },
  })
}

async function updateBilling() {
  await $httpFetch('/bm/client/addresses/v2/billing', {
    method: 'POST',
    body: {
      city: props.customerDetails.billingAddress.city,
      country: getCountryFromBouyguesCountry(
        props.customerDetails.billingAddress.country,
      ),
      firstName: props.customerDetails.firstName,
      lastName: props.customerDetails.lastName,
      postalCode: props.customerDetails.billingAddress.postCode,
      street: `${props.customerDetails.billingAddress.streetNumber} ${props.customerDetails.billingAddress.streetName}`,
    },
  })
}

async function handleReplacement(listingPublicId: string) {
  isLoading.value = true
  try {
    await addServiceToCart(listingPublicId)
    await updateBilling()
    error.value = null
  } catch (err) {
    error.value = err
    throw err
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  tracking.setContext({
    bouygues_in_store_magic_link_opened: true,
  })

  if (props.shopId && props.cartSelectionId) {
    tracking.setContext({
      bouygues_in_store_shop_id: props.shopId,
      bouygues_in_store_cart_selection_id: props.cartSelectionId,
    })
  }

  try {
    await $httpFetch('/bm/cart', { method: 'DELETE' })

    await $httpFetch('/bm/cart/add_product', {
      method: 'POST',
      body: {
        listing_id: props.listingPk,
      },
    })

    await addServiceToCart(props.listingPublicId)
    await updateBilling()
  } catch (err) {
    error.value = err
    throw err
  } finally {
    isLoading.value = false
  }
})
</script>
